import React, { useCallback, useState, useEffect } from "react";
import { useQuery, useAction } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { useOrganizationSync } from "@/hooks/useOrganizationSync";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Check,
  Rocket,
  Users,
  Sprout,
  Flame,
  Sparkles,
  Plus,
  Minus,
  Zap,
  Lock,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface StripePlan {
  id: string;
  stripeProductId: string;
  stripePriceId: string | null;
  stripePriceIdMonthly: string | null;
  stripePriceIdYearly: string | null;
  name: string;
  description: string;
  price: number;
  priceDisplay: string;
  credits: number;
  features: string[];
  isRecurring: boolean;
  interval: string | null;
}

interface TopUpProduct {
  id: string;
  stripeProductId: string | null;
  stripePriceId: string | null;
  name: string;
  description: string;
  price: number;
  priceDisplay: string;
  credits: number;
}

export function PlansTab() {
  const { localOrganization, isLoading: isOrgSyncLoading } = useOrganizationSync();
  const orgId =
    localOrganization && !localOrganization.isPersonalWorkspace ? localOrganization._id : undefined;
  const personalWorkspaceId = localOrganization?.isPersonalWorkspace
    ? localOrganization._id
    : undefined;

  const subscriptionData = useQuery(
    api.subscriptions.getOrganizationSubscription,
    isOrgSyncLoading ? "skip" : { orgId, personalWorkspaceId }
  );
  const topUpEligibility = useQuery(
    api.subscriptions.canPurchaseTopUp,
    isOrgSyncLoading ? "skip" : { orgId, personalWorkspaceId }
  );
  const createCheckout = useAction(api.subscriptionsActions.createCheckoutSession);
  const createTopUpCheckout = useAction(api.subscriptionsActions.createTopUpCheckoutSession);
  const getStripeProducts = useAction(api.subscriptionsActions.getStripeProducts);
  const getTopUpProduct = useAction(api.subscriptionsActions.getTopUpProduct);
  const changePlan = useAction(api.subscriptionsActions.changeSubscriptionPlan);

  const [plans, setPlans] = useState<StripePlan[]>([]);
  const [topUpProduct, setTopUpProduct] = useState<TopUpProduct | null>(null);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [isStripeUnavailable, setIsStripeUnavailable] = useState(false);
  const [topUpQuantity, setTopUpQuantity] = useState(1);
  const [isLoadingTopUp, setIsLoadingTopUp] = useState(false);
  const [upgradingPlanId, setUpgradingPlanId] = useState<string | null>(null);
  const [showPlanRequiredAlert, setShowPlanRequiredAlert] = useState(false);
  const [showUpgradeAlert, setShowUpgradeAlert] = useState(false);
  const [showTopUpAlert, setShowTopUpAlert] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState<{ priceId: string; planId: string } | null>(
    null
  );

  const currentPlanId = subscriptionData?.organization?.planType || "basic";
  const hasPaidPlan =
    topUpEligibility !== undefined
      ? topUpEligibility.canPurchase
      : currentPlanId !== "basic" && subscriptionData?.subscription?.status === "active";

  // Fetch Stripe products on mount
  useEffect(() => {
    async function fetchProducts() {
      try {
        setIsLoadingPlans(true);
        const [productsResult, topUpResult] = await Promise.all([
          getStripeProducts(),
          getTopUpProduct(),
        ]);
        setPlans(productsResult);
        setTopUpProduct(topUpResult);

        // If no products have a stripePriceId (except basic), Stripe might be unconfigured or unreachable
        const hasStripeConfig = productsResult.some((p) => p.id !== "basic" && p.stripePriceId);
        setIsStripeUnavailable(!hasStripeConfig);
      } catch (error) {
        console.error("Failed to fetch products:", error);
        setIsStripeUnavailable(true);
      } finally {
        setIsLoadingPlans(false);
      }
    }
    fetchProducts();
  }, [getStripeProducts, getTopUpProduct]);

  const confirmUpgrade = async () => {
    if (!pendingUpgrade) return;

    try {
      await changePlan({ priceId: pendingUpgrade.priceId });
      window.location.reload(); // Simple refresh to pick up new plan
    } catch (error) {
      console.error("Failed to upgrade subscription:", error);
      alert("Failed to upgrade subscription. Please try again or contact support.");
    } finally {
      setShowUpgradeAlert(false);
      setPendingUpgrade(null);
    }
  };

  const handleUpgrade = useCallback(
    async (stripePriceId: string, planId: string) => {
      if (!stripePriceId) {
        console.error("No Stripe price ID for this plan");
        return;
      }

      // If user has a paid plan (not just basic/free), try to upgrade directly
      const isPaidPlan = currentPlanId !== "basic";

      if (isPaidPlan) {
        setPendingUpgrade({ priceId: stripePriceId, planId });
        setShowUpgradeAlert(true);
      } else {
        setUpgradingPlanId(planId);
        try {
          const checkout = await createCheckout({
            priceId: stripePriceId,
            orgId,
            personalWorkspaceId,
          });
          if (checkout?.url) {
            window.location.href = checkout.url;
          }
        } catch (error) {
          console.error("Failed to upgrade subscription:", error);
          alert("Failed to upgrade subscription. Please try again or contact support.");
        } finally {
          setUpgradingPlanId(null);
        }
      }
    },
    [createCheckout, changePlan, currentPlanId]
  );

  const handleTopUpPurchase = useCallback(() => {
    // Check if user has an active paid plan before allowing top-up purchase
    if (!hasPaidPlan) {
      setShowPlanRequiredAlert(true);
      return;
    }
    if (!topUpProduct?.stripePriceId) {
      console.error("No Stripe price ID for top-up product");
      return;
    }
    setShowTopUpAlert(true);
  }, [hasPaidPlan, topUpProduct]);

  const confirmTopUpPurchase = async () => {
    if (!hasPaidPlan) {
      setShowTopUpAlert(false);
      setShowPlanRequiredAlert(true);
      return;
    }
    if (!topUpProduct?.stripePriceId) return;

    setIsLoadingTopUp(true);
    try {
      const checkout = await createTopUpCheckout({
        quantity: topUpQuantity,
        orgId,
        personalWorkspaceId,
      });
      if (checkout?.url) {
        window.location.href = checkout.url;
      }
    } catch (error: any) {
      console.error("Failed to create top-up checkout session:", error);
      alert(
        error?.message ||
        "Top-off credits can only be purchased when you have an active subscription plan."
      );
    } finally {
      setIsLoadingTopUp(false);
      setShowTopUpAlert(false);
    }
  };

  if (isLoadingPlans || isOrgSyncLoading || subscriptionData === undefined) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Separate individual and team plans
  const individualPlans = plans.filter(
    (p) =>
      !p.id.startsWith("team") &&
      p.id !== "topup" &&
      !p.name.toLowerCase().includes("top-up") &&
      !p.name.toLowerCase().includes("credit pack")
  );
  const teamPlans = plans.filter((p) => p.id.startsWith("team"));

  const getPlanIcon = (planId: string) => {
    if (planId.startsWith("team")) return Users;
    if (planId === "professional") return Rocket;
    if (planId === "starter") return Flame;
    return Sprout; // Default for basic plan
  };

  const getPlanGradient = (planId: string, isCurrentPlan: boolean) => {
    if (isCurrentPlan) return "from-blue-50 to-indigo-50 border-blue-200";
    if (planId === "professional") return "from-purple-50 to-pink-50";
    if (planId.startsWith("team")) return "from-green-50 to-emerald-50";
    return "from-gray-50 to-slate-50";
  };

  // Features matching the pricing page (excluding user and credit counts)
  const getPlanFeatures = (planId: string): string[] => {
    switch (planId) {
      case "basic":
        return [
          "1 user",
          "Unlimited bids",
          "Unlimited clients & leads",
          "Equipment & supplies",
          "Ratings & reviews",
          "Service projects",
          "Subcontractor management",
        ];
      case "starter":
        return [
          "Everything in Basic",
          "5 Knowledge Base file uploads",
          "AI Business Assistant",
          "AI Document Writer",
          "AI generated Proposals",
          "AI generated Service Agreements",
        ];
      case "professional":
        return ["Everything in Starter", "50 Knowledge Base file uploads"];
      case "team10k":
        return [
          "Unlimited team members",
          "Team calendar",
          "100 Knowledge Base file uploads",
        ];
      case "team25k":
        return [
          "Unlimited team members",
          "Team calendar",
          "200 Knowledge Base file uploads",
        ];
      case "team50k":
        return [
          "Unlimited team members",
          "Team calendar",
          "500 Knowledge Base file uploads",
        ];
      default:
        return [];
    }
  };

  return (
    <div className="space-y-8">
      {isStripeUnavailable && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <Flame className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-medium text-amber-900">Payment System Unavailable</p>
            <p className="mt-1 text-sm text-amber-700">
              We're currently experiencing issues connecting to our payment provider. You can still
              view available plans, but subscription changes are temporarily disabled.
            </p>
          </div>
        </div>
      )}

      {/* Individual Plans */}
      <div>
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Individual Plans</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {individualPlans.map((plan: any) => {
            const Icon = getPlanIcon(plan.id);
            const isCurrentPlan = plan.id === currentPlanId;

            return (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all hover:shadow-lg ${isCurrentPlan ? "ring-2 ring-blue-500" : ""}`}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${getPlanGradient(plan.id, isCurrentPlan)} opacity-50`}
                />
                <CardHeader className="relative pb-2">
                  <div className="flex items-center justify-between">
                    <div className="rounded-lg bg-white/80 p-2 shadow-sm">
                      <Icon className="h-5 w-5 text-gray-700" />
                    </div>
                    {isCurrentPlan && (
                      <Badge variant="default" className="bg-blue-600">
                        Current Plan
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="mt-3 text-xl">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="relative space-y-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">{plan.priceDisplay}</span>
                  </div>

                  <ul className="space-y-2">
                    {getPlanFeatures(plan.id).map((feature: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="mt-4 w-full"
                    variant={isCurrentPlan ? "outline" : "default"}
                    disabled={
                      isCurrentPlan ||
                      plan.id === "basic" ||
                      !plan.stripePriceId ||
                      upgradingPlanId === plan.id
                    }
                    onClick={() => plan.stripePriceId && handleUpgrade(plan.stripePriceId, plan.id)}
                  >
                    {upgradingPlanId === plan.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      "Current Plan"
                    ) : plan.id === "basic" ? (
                      "Free"
                    ) : !plan.stripePriceId ? (
                      "Unavailable"
                    ) : (
                      "Upgrade"
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Team Plans */}
      <div>
        <h3 className="mb-2 text-lg font-semibold text-gray-900">Team Plans</h3>
        <p className="mb-4 text-sm text-gray-600">
          Pooled credits for field teams with unlimited users. Perfect for automated field
          operations.
        </p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {teamPlans.map((plan: any) => {
            const Icon = getPlanIcon(plan.id);
            const isCurrentPlan = plan.id === currentPlanId;

            return (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all hover:shadow-lg ${isCurrentPlan ? "ring-2 ring-blue-500" : ""}`}
              >
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${getPlanGradient(plan.id, isCurrentPlan)} opacity-50`}
                />
                <CardHeader className="relative pb-2">
                  <div className="flex items-center justify-between">
                    <div className="rounded-lg bg-white/80 p-2 shadow-sm">
                      <Icon className="h-5 w-5 text-gray-700" />
                    </div>
                    {isCurrentPlan && (
                      <Badge variant="default" className="bg-blue-600">
                        Current Plan
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="mt-3 text-xl">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="relative space-y-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-gray-900">{plan.priceDisplay}</span>
                  </div>

                  <ul className="space-y-2">
                    {getPlanFeatures(plan.id).map((feature: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Button
                    className="mt-4 w-full"
                    variant={isCurrentPlan ? "outline" : "default"}
                    disabled={isCurrentPlan || !plan.stripePriceId || upgradingPlanId === plan.id}
                    onClick={() => plan.stripePriceId && handleUpgrade(plan.stripePriceId, plan.id)}
                  >
                    {upgradingPlanId === plan.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrentPlan ? (
                      "Current Plan"
                    ) : (
                      "Upgrade"
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Top-Ups Section */}
      {topUpProduct && (
        <div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">Credit Top-Ups</h3>
          <p className="mb-4 text-sm text-gray-600">
            Need more credits? Purchase top-up packs anytime. These credits never expire.
          </p>
          <Card className="relative overflow-hidden transition-all hover:shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50 to-orange-50 opacity-50" />
            <CardHeader className="relative pb-2">
              <div className="flex items-center justify-between">
                <div className="rounded-lg bg-white/80 p-2 shadow-sm">
                  <Zap className="h-5 w-5 text-amber-600" />
                </div>
                <Badge variant="outline" className="border-amber-500 bg-amber-50 text-amber-700">
                  Never Expires
                </Badge>
              </div>
              <CardTitle className="mt-3 text-xl">{topUpProduct.name}</CardTitle>
              <CardDescription className="text-sm">{topUpProduct.description}</CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900">
                  {topUpProduct.priceDisplay}
                </span>
                <span className="text-sm text-gray-500">/pack</span>
              </div>

              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm text-gray-600">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                  {topUpProduct.credits.toLocaleString()} credits per pack
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-600">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                  Credits never expire
                </li>
                <li className="flex items-start gap-2 text-sm text-gray-600">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-500" />
                  Use anytime you need extra capacity
                </li>
              </ul>

              {/* Quantity Selector */}
              <div className="flex items-center justify-between border-t pt-2">
                <span className="text-sm font-medium text-gray-700">Quantity:</span>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setTopUpQuantity(Math.max(1, topUpQuantity - 1))}
                    disabled={topUpQuantity <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-8 text-center text-lg font-semibold">{topUpQuantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setTopUpQuantity(Math.min(10, topUpQuantity + 1))}
                    disabled={topUpQuantity >= 10}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between rounded-lg bg-amber-50 px-3 py-2">
                <span className="text-sm font-medium text-gray-700">Total:</span>
                <div className="text-right">
                  <span className="text-xl font-bold text-gray-900">
                    ${(topUpProduct.price / 100) * topUpQuantity}
                  </span>
                  <span className="ml-2 text-sm text-gray-500">
                    ({(topUpQuantity * topUpProduct.credits).toLocaleString()} credits)
                  </span>
                </div>
              </div>

              <Button
                className={`mt-2 w-full ${
                  hasPaidPlan
                    ? "bg-amber-600 hover:bg-amber-700"
                    : "bg-slate-800 hover:bg-slate-900 text-white"
                }`}
                onClick={handleTopUpPurchase}
                disabled={isLoadingTopUp || !topUpProduct.stripePriceId}
              >
                {isLoadingTopUp ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : !topUpProduct.stripePriceId ? (
                  "Not Available"
                ) : !hasPaidPlan ? (
                  <>
                    <Lock className="mr-2 h-4 w-4 text-amber-400" />
                    Subscription Plan Required
                  </>
                ) : (
                  `Purchase ${topUpQuantity} Pack${topUpQuantity > 1 ? "s" : ""}`
                )}
              </Button>
              {!hasPaidPlan && (
                <p className="mt-2 text-center text-xs text-amber-700 font-medium flex items-center justify-center gap-1.5">
                  <Lock className="h-3.5 w-3.5 shrink-0" />
                  Top-off credits are an add-on exclusive to active plan subscribers.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 text-blue-600" />
            <div>
              <p className="text-sm font-medium text-blue-900">Need a custom plan?</p>
              <p className="mt-1 text-sm text-blue-700">
                Contact our sales team for enterprise pricing and custom credit allocations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Plan Required Alert Dialog */}
      <AlertDialog open={showPlanRequiredAlert} onOpenChange={setShowPlanRequiredAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-amber-600" />
              Subscription Plan Required
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-sm text-gray-600">
              <span>
                Top-off credit packs are add-ons designed to supplement an active subscription.
                Please subscribe to one of our plans (Starter, Professional, or Team) before purchasing top-up credits.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowPlanRequiredAlert(false)}>
              Close
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowPlanRequiredAlert(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              View Plans
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upgrade Confirmation Alert */}
      <AlertDialog open={showUpgradeAlert} onOpenChange={setShowUpgradeAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Plan Upgrade</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to upgrade your subscription? The difference in price will be
              charged immediately to your card on file.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingUpgrade(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUpgrade}>Confirm Upgrade</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Top-Up Confirmation Alert */}
      <AlertDialog open={showTopUpAlert} onOpenChange={setShowTopUpAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Credit Purchase</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to purchase {topUpQuantity} credit pack
              {topUpQuantity > 1 ? "s" : ""}?
              <br />
              <br />
              <strong>
                Total: ${(((topUpProduct?.price || 0) / 100) * topUpQuantity).toFixed(2)}
              </strong>{" "}
              for {(topUpQuantity * (topUpProduct?.credits || 0)).toLocaleString()} credits.
              <br />
              This amount will be charged immediately to your card on file.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmTopUpPurchase}>Confirm Purchase</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
